import { PlanPackageDetails } from "features/types";
import { shopV3Dataset } from "lib/sanity";
import {
  BackendPlanV3,
  ProductVariantV3,
  ShopCatalogV3,
  ShopCategoryV3,
  ShopProductV3,
} from "lib/sanity.types";
import tinycolor from "tinycolor2";
import { formatCurrency } from "utils/appUtils";

// Prototype warm palette (the fgm design tokens) shared by the V3 screens.
export const PAPER = "#fffdfb";
export const PANEL = "#f6f1ec";
export const LINE = "#e2d8d3";

export const isValidEmail = (value: string) =>
  /^\S+@\S+\.\S+$/.test(value.trim());

// Tenant-color hero gradient with the prototype's faint diagonal stripes
// (fgm-hero-v2 / fgm-plan-hero). CSS backgroundImage — web-only screens.
export const heroGradient = (primaryColor: string) =>
  `repeating-linear-gradient(132deg, transparent 0 32px, rgba(0,0,0,0.14) 33px 46px), linear-gradient(135deg, ${primaryColor} 0%, ${tinycolor(
    primaryColor,
  )
    .darken(8)
    .toHexString()} 58%, ${tinycolor(primaryColor).darken(16).toHexString()} 116%)`;

// CMS → backend plan id resolution derives from the SAME environment
// decision as the dataset choice, so content and plan ids cannot drift:
// the demo environment resolves demoIds from the demo dataset; dev,
// stage, and production resolve prodIds from the production dataset.
// (Dev and stage databases have their own id sequences, so prodIds only
// resolve there when their data mirrors production — the same limitation
// the legacy shop pages have.)
export const resolvePlanPackageId = (
  backendPlan?: BackendPlanV3,
): number | undefined =>
  shopV3Dataset === "demo" ? backendPlan?.demoId : backendPlan?.prodId;

// A variant that resolved to a live, purchasable backend plan package.
export interface LiveVariant {
  variant: ProductVariantV3;
  planPackage: PlanPackageDetails;
}

export interface LiveProduct {
  product: ShopProductV3;
  variants: LiveVariant[];
}

export interface LiveCategory {
  category: ShopCategoryV3;
  products: LiveProduct[];
}

// Merge the CMS catalog with the live plan packages: drop variants whose
// plan is missing/disabled/unavailable, then products with no live
// variants, then categories with no live products. The remaining tabs
// redistribute automatically.
export const buildLiveCatalog = (
  catalog: ShopCatalogV3,
  planPackages: PlanPackageDetails[],
): LiveCategory[] => {
  const byId = new Map(planPackages.map((pp) => [pp.id, pp]));

  return (catalog.categories ?? [])
    .filter((category) => !!category && category.visible !== false)
    .map((category) => ({
      category,
      products: (category.products ?? [])
        .filter((product): product is ShopProductV3 => !!product)
        .map((product) => ({
          product,
          variants: (product.variants ?? []).flatMap<LiveVariant>(
            (variant) => {
              const planPackageId = resolvePlanPackageId(variant.backendPlan);
              const planPackage =
                planPackageId === undefined
                  ? undefined
                  : byId.get(planPackageId);
              if (!planPackage || planPackage.disabled || !planPackage.available)
                return [];
              return [{ variant, planPackage }];
            },
          ),
        }))
        .filter((product) => product.variants.length > 0),
    }))
    .filter((entry) => entry.products.length > 0);
};

export const monthlyPriceOf = (planPackage: PlanPackageDetails): number => {
  const monthly = Number(planPackage.monthlyPrice);
  if (Number.isFinite(monthly) && monthly > 0) return monthly;
  const full = Number(planPackage.fullPrice);
  if (!Number.isFinite(full) || full === 0) return 0;
  return full / (planPackage.numBillingMonths || 12);
};

export const fullPriceOf = (planPackage: PlanPackageDetails): number => {
  const full = Number(planPackage.fullPrice);
  return Number.isFinite(full) ? full : 0;
};

export type PriceDisplayStrategy = ShopCatalogV3["priceDisplayStrategy"];

// The single displayed-price rule: a "/mo" price is only ever shown for a
// plan that actually offers MONTHLY billing; everything else shows its
// full price. All price labels derive from this one function.
const displayedPrice = (
  planPackage: PlanPackageDetails,
  strategy: PriceDisplayStrategy,
): { amount: number; suffix: string } => {
  const useMonthly =
    strategy === "lowest-monthly" &&
    planPackage.billingIntervals.includes("MONTHLY");
  return useMonthly
    ? { amount: monthlyPriceOf(planPackage), suffix: "/mo" }
    : { amount: fullPriceOf(planPackage), suffix: "" };
};

// "Free", "$99", or "$8.25/mo" for one plan, per the catalog strategy.
export const unitPriceLabel = (
  planPackage: PlanPackageDetails,
  strategy: PriceDisplayStrategy,
): string => {
  if (fullPriceOf(planPackage) === 0) return "Free";
  const { amount, suffix } = displayedPrice(planPackage, strategy);
  return `${formatCurrency(amount)}${suffix}`;
};

// "From $8.25/mo" across a category's live variants; "" when it has none.
export const categoryPriceLabel = (
  products: LiveProduct[],
  strategy: PriceDisplayStrategy,
): string => {
  const candidates = products
    .flatMap((product) => product.variants)
    .map(({ planPackage }) => planPackage);
  if (candidates.length === 0) return "";
  const cheapest = candidates.reduce((best, planPackage) =>
    displayedPrice(planPackage, strategy).amount <
    displayedPrice(best, strategy).amount
      ? planPackage
      : best,
  );
  if (fullPriceOf(cheapest) === 0) return "Free";
  return `From ${unitPriceLabel(cheapest, strategy)}`;
};

// Email capture is ON by default for every plan; Sanity stores only
// explicit disables, checked from most to least specific.
export const isEmailCaptureEnabled = (
  catalog: ShopCatalogV3,
  product: ShopProductV3,
  variant: ProductVariantV3,
): boolean =>
  !(
    variant.emailCaptureDisabled ??
    product.emailCaptureDisabled ??
    catalog.emailCaptureDisabled ??
    false
  );

// Default selected variant: the merchandised "most popular" one, else the first.
export const defaultVariant = (variants: LiveVariant[]): LiveVariant =>
  variants.find(({ variant }) => variant.popular) ?? variants[0];

export interface ResolvedUpgrade {
  product: LiveProduct;
  live: LiveVariant;
  headline?: string;
  note?: string;
  benefits: string[];
}

// The upgrade offer as the checkout consumes it: the screen resolves the
// offer and owns applied/dismissed state; the checkout renders the tile
// and reports accept/undo/dismiss back.
export interface CheckoutUpgrade {
  applied: boolean;
  destinationProduct: ShopProductV3;
  destinationLive: LiveVariant;
  sourcePlanPackage: PlanPackageDetails;
  headline?: string;
  note?: string;
  benefits: string[];
  onAccept: () => void;
  onUndo: () => void;
  onDismiss: () => void;
}

// Resolve a variant's configured upgrade offer against the live catalog.
// Returns null when none is configured or the destination has no live
// backend plan — the "zero or one eligible offer" rule falls out of the
// same availability merge as the rest of the storefront.
export const resolveUpgradeOffer = (
  liveCatalog: LiveCategory[],
  sourceVariant: LiveVariant["variant"],
): ResolvedUpgrade | null => {
  const offer = sourceVariant.upgradeOffer;
  if (!offer?.product?._ref || !offer.variantKey) return null;
  for (const entry of liveCatalog) {
    for (const product of entry.products) {
      if (product.product._id !== offer.product._ref) continue;
      const live = product.variants.find(
        ({ variant }) => variant.key === offer.variantKey,
      );
      if (!live) return null;
      return {
        product,
        live,
        headline: offer.headline,
        note: offer.note,
        benefits: offer.benefits ?? [],
      };
    }
  }
  return null;
};
